import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Form, Alert, Card } from 'react-bootstrap';
import api from '../services/api';
import Header from '../components/layout/Header';
import brasao from '../assets/img/brasao.png';
import '../assets/css/login.css';
import '../assets/css/styles.css';

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tokenValid, setTokenValid] = useState(false);

  // Valida token ao carregar a página
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    
    if (!tokenParam) {
      setError('Token não fornecido. Solicite um novo link de redefinição.');
      setValidating(false);
      return;
    }

    setToken(tokenParam);
    validateToken(tokenParam);
  }, [searchParams]);

  const validateToken = async (tokenToValidate) => {
    try {
      const response = await api.post('/password/validate-token', {
        token: tokenToValidate
      });

      if (response.data.valid) {
        setTokenValid(true);
        setEmail(response.data.email);
      }
    } catch (err) {
      setTokenValid(false);
      setError(err.response?.data?.message || 'Token inválido ou expirado.');
    } finally {
      setValidating(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validações
    if (!password || !passwordConfirmation) {
      setError('Por favor, preencha todos os campos.');
      return;
    }

    if (password.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres.');
      return;
    }

    if (password !== passwordConfirmation) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/password/reset', {
        token,
        password,
        password_confirmation: passwordConfirmation
      });

      if (response.data.success) {
        setSuccess(response.data.message);
        
        // Redireciona para login após 3 segundos
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        setError(Object.values(errors).flat().join(' '));
      } else {
        setError(err.response?.data?.message || 'Erro ao redefinir senha. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-container">
      <Header />
      <div className="login-content-wrapper">
        <h1 className="hero-title-top">Sistema de Movimentação</h1>
        
        <div className="login-modal-content">
          <div className="login-modal-header">
            <div className="text-center mb-3">
              <img 
                src={brasao}
                alt="Brasão SSE-DF" 
                className="mb-2" 
                height="80"
              />
              <h3>Redefinir Senha</h3>
            </div>
          </div>
          <div className="login-modal-body">
            {validating ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Validando...</span>
                </div>
                <p className="text-light">Validando token...</p>
              </div>
            ) : !tokenValid ? (
              <div className="text-center">
                <Alert variant="danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error || 'Token inválido ou expirado.'}
                </Alert>
                <Button 
                  onClick={() => navigate('/login')}
                  className="mt-3 btn-login"
                >
                  Voltar para Login
                </Button>
              </div>
            ) : success ? (
              <div className="text-center">
                <Alert variant="success">
                  <i className="bi bi-check-circle me-2"></i>
                  {success}
                </Alert>
                <p className="text-light mt-3">
                  Redirecionando para o login...
                </p>
              </div>
            ) : (
              <form onSubmit={handleResetPassword}>
                <div className="mb-3">
                  <p className="text-light">
                    Criando nova senha para: <strong>{email}</strong>
                  </p>
                </div>

                <div className="form-floating mb-4">
                  <input
                    type="password"
                    id="password"
                    className="form-control glass-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder=""
                    required
                    disabled={loading}
                    autoFocus
                  />
                  <label htmlFor="password">Nova Senha</label>
                </div>

                <div className="form-floating mb-4">
                  <input
                    type="password"
                    id="passwordConfirmation"
                    className="form-control glass-input"
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    placeholder=""
                    required
                    disabled={loading}
                  />
                  <label htmlFor="passwordConfirmation">Confirmar Senha</label>
                </div>

                <div className="alert alert-info small mb-4 reset-password-info">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Requisitos da senha:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Mínimo de 8 caracteres</li>
                    <li>Recomendado: letras, números e símbolos</li>
                  </ul>
                </div>

                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                <div className="d-grid mt-4">
                  <Button 
                    type="submit" 
                    size="lg" 
                    disabled={loading}
                    className="btn-login"
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Redefinindo...
                      </>
                    ) : (
                      'Redefinir Senha'
                    )}
                  </Button>
                </div>

                <div className="text-center mt-3">
                  <a 
                    href="#" 
                    onClick={(e) => { e.preventDefault(); navigate('/login'); }}
                    className="text-decoration-none text-light"
                  >
                    Voltar para o Login
                  </a>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
